import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Button, message, Modal } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  BellOutlined,
  UserOutlined,
  DashboardOutlined,
  TransactionOutlined,
  SettingOutlined,
  WalletOutlined,
  PayCircleOutlined,
  LinkOutlined,
  KeyOutlined,
  BankOutlined,
  QrcodeOutlined,
  BookOutlined,
  ApiOutlined,
  SafetyOutlined,
  AppstoreOutlined
} from '@ant-design/icons';

// Import merchant components
import MerchantLogin from './components/merchant/MerchantLogin';
import MerchantDashboard from './pages/merchant/MerchantDashboard';
import MerchantTransactions from './pages/merchant/MerchantTransactions';
import MerchantPayments from './pages/merchant/MerchantPayments';
import MerchantSettings from './pages/merchant/MerchantSettings';
import MerchantFinance from './pages/merchant/MerchantFinance';
import MerchantCashIn from './pages/merchant/MerchantCashIn';
import MerchantCashOut from './pages/merchant/MerchantCashOut';
import MerchantCashInOut from './pages/merchant/MerchantCashInOut';
import MerchantWithdrawal from './pages/merchant/MerchantWithdrawal';
import MerchantCallbacks from './pages/merchant/MerchantCallbacks';
import MerchantAPIKeys from './pages/merchant/MerchantAPIKeys';
import MerchantProfile from './pages/merchant/MerchantProfile';
import MerchantSnapV2Credentials from './pages/merchant/MerchantSnapV2Credentials';
import MerchantSecurity from './pages/merchant/MerchantSecurity';
import MerchantProducts from './pages/merchant/MerchantProducts';
import QRISPayment from './pages/QRISPayment';

const { Content, Header, Sider } = Layout;

function MerchantApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not auth, true = authenticated
  const [merchant, setMerchant] = useState(null);

  // Basic debug - see if component renders at all
  console.log('🎯 MerchantApp Component Rendering');

  // Protected Route Component for KYC verified routes
  const KYCProtectedRoute = ({ children }) => {
    console.log('🛡️ KYC Route Check:', {
      isKYCVerified,
      isKYCPending,
      kyc_status: merchant?.kyc_status,
      isAuthenticated
    });

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      console.log('🔐 Not authenticated, redirecting to login');
      return <Navigate to="/merchant" replace />;
    }

    // Always render children, but let individual components handle KYC restrictions
    console.log('✅ Rendering protected route');
    return children;
  };

  // Fallback component for direct URL access
  const DirectURLHandler = ({ children }) => {
    console.log('🎯 DirectURLHandler:', {
      isKYCVerified,
      isKYCPending,
      kyc_status: merchant?.kyc_status
    });

    // Show KYC warning modal for direct access to protected routes
    React.useEffect(() => {
      if (isKYCPending) {
        Modal.warning({
          title: '⚠️ Verifikasi KYC Diperlukan',
          content: (
            <div>
              <p><strong>Status KYC: PENDING</strong></p>
              <br />
              <p>Untuk mengakses halaman ini, Anda perlu menyelesaikan verifikasi KYC terlebih dahulu.</p>
              <br />
              <p><strong>Dokumen yang diperlukan:</strong></p>
              <ul>
                <li>KTP/SIM/Paspor yang valid</li>
                <li>NPWP (jika ada)</li>
                <li>Dokumen kepemilikan usaha</li>
              </ul>
              <br />
              <p><strong>Waktu proses:</strong> 1-3 hari kerja</p>
              <p><strong>Hubungi:</strong> support@arendo.co.id</p>
            </div>
          ),
          width: 500,
          okText: 'Mengerti',
          onOk: () => {
            // Redirect to dashboard after closing modal
            window.location.href = '/merchant/dashboard';
          }
        });
      }
    }, [isKYCPending]);

    if (isKYCVerified) {
      return children;
    }

    // For KYC pending, show a simple loading state while modal appears
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Memeriksa verifikasi KYC...</div>
        <Button type="primary" onClick={() => window.location.href = '/merchant/dashboard'}>
          Kembali ke Dashboard
        </Button>
      </div>
    );
  };

  // Simple route handler for finance
  const FinanceRouteHandler = () => {
    console.log('🎯 FinanceRouteHandler Called', { isKYCPending, isKYCVerified });

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      console.log('🔐 Not authenticated, redirecting to login');
      return <Navigate to="/merchant" replace />;
    }

    // If KYC pending, show warning and redirect
    if (isKYCPending) {
      console.log('⚠️ KYC Pending, showing warning');
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '18px', color: '#faad14' }}>
            ⚠️ Verifikasi KYC Diperlukan
          </div>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <p>Status KYC Anda: <strong>PENDING</strong></p>
            <p>Untuk mengakses halaman Keuangan, Anda perlu menyelesaikan verifikasi KYC terlebih dahulu.</p>
          </div>
          <Button type="primary" onClick={() => window.location.href = '/merchant/dashboard'}>
            Kembali ke Dashboard
          </Button>
        </div>
      );
    }

    // If KYC verified, render the actual component
    console.log('✅ KYC Verified, rendering Finance component');
    return <MerchantFinance />;
  };

  // Check authentication on mount
  useEffect(() => {
    console.log('🔍 useEffect running');
    const token = localStorage.getItem('merchant_access_token');
    const merchantData = localStorage.getItem('merchant_user');

    let parsedMerchantData = null;
    if (merchantData) {
      try {
        parsedMerchantData = JSON.parse(merchantData);
      } catch (error) {
        console.error('❌ Failed to parse merchantData from localStorage:', error);
        // Clear corrupted data
        localStorage.removeItem('merchant_user');
        localStorage.removeItem('merchant_access_token');
        setIsAuthenticated(false);
        return;
      }
    }

    console.log('🔍 Mount Debug:', {
      hasToken: !!token,
      hasMerchantData: !!merchantData,
      merchantData: parsedMerchantData
    });

    if (token && parsedMerchantData) {
      console.log('📦 Setting merchant from localStorage:', {
        email: parsedMerchantData.email,
        kyc_status: parsedMerchantData.kyc_status
      });
      setMerchant(parsedMerchantData);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogin = (merchantData) => {
    // Debug: Log merchant data
    console.log('🔐 MerchantApp Login Debug:', {
      merchantData: merchantData.member,
      kyc_status: merchantData.member?.kyc_status,
      email: merchantData.member?.email
    });

    setMerchant(merchantData.member);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('merchant_access_token');
    localStorage.removeItem('merchant_refresh_token');
    localStorage.removeItem('merchant_user');
    setIsAuthenticated(false);
    setMerchant(null);

    message.success('Logged out successfully');
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case 'profile':
        navigate('/merchant/profile');
        break;
      case 'settings':
        navigate('/merchant/settings');
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    console.log('⏳ Showing loading state');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f0f2f5'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // If not authenticated, show login form
  if (isAuthenticated === false) {
    return <MerchantLogin onLogin={handleLogin} />;
  }

  // Debug: Log merchant KYC status will be handled below

  const baseMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    // QRIS Payment menu - Hidden for now
    // {
    //   key: 'qris-payment',
    //   icon: <QrcodeOutlined />,
    //   label: 'QRIS Payment',
    // },
  ];

  const verifiedMenuItems = [
    {
      key: 'finance',
      icon: <WalletOutlined />,
      label: 'Keuangan',
    },
    {
      key: 'cash-flow',
      icon: <PayCircleOutlined />,
      label: 'Cash In/Out',
    },
    {
      key: 'withdrawal',
      icon: <BankOutlined />,
      label: 'Withdrawal',
    },
    {
      key: 'transactions',
      icon: <TransactionOutlined />,
      label: 'Transaksi',
    },
    {
      key: 'callbacks',
      icon: <LinkOutlined />,
      label: 'Callback',
    },
    // API Keys - Hidden (merged into Security)
    // {
    //   key: 'api-keys',
    //   icon: <KeyOutlined />,
    //   label: 'API Keys',
    // },
    {
      key: 'products',
      icon: <AppstoreOutlined />,
      label: 'Produk',
    },
    {
      key: 'snap-v2-credentials',
      icon: <ApiOutlined />,
      label: 'SNAP BI V2',
    },
    // API Documentation menu - Hidden for now
    // {
    //   key: 'api-documentation',
    //   icon: <BookOutlined />,
    //   label: 'API Documentation',
    // },
    {
      key: 'security',
      icon: <SafetyOutlined />,
      label: 'Keamanan',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Pengaturan',
    },
  ];

  // Fix KYC status detection - ensure boolean values
  const isKYCPending = merchant?.kyc_status === 'pending' || false;
  const isKYCVerified = merchant?.kyc_status === 'verified' || false;

  // Debug: Log merchant KYC status
  console.log('🔍 Merchant Debug:', {
    merchant: merchant,
    kyc_status: merchant?.kyc_status,
    isKYCPending,
    isKYCVerified,
    email: merchant?.email
  });

  // Always show all menu items but with KYC indicators
  const menuItems = [
    ...baseMenuItems,
    ...verifiedMenuItems.map(item => ({
      ...item,
      label: isKYCPending ? (
        <span style={{ color: '#faad14', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {item.label} ⚠️
        </span>
      ) : item.label
    }))
  ];

  // Add status info menu for pending KYC
  if (isKYCPending) {
    menuItems.push({
      key: 'kyc-status',
      icon: <SettingOutlined />,
      label: (
        <span style={{ color: '#faad14' }}>
          ⚠️ Status KYC Pending
        </span>
      ),
      disabled: true,
    });
  }

  // Debug: Log menu items
  console.log('📋 Menu Debug:', {
    isKYCPending,
    isKYCVerified,
    menuItems: menuItems.map(item => ({ key: item.key, label: item.label }))
  });

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Pengaturan',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Keluar',
      danger: true,
    },
  ];

  return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width="15%"
          collapsedWidth="5%"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, #1e40af 0%, #3b82f6 100%)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="flex items-center justify-center h-20 text-white fade-in">
            {collapsed ? (
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-xl font-bold shadow-lg">
                MG
              </div>
            ) : (
              <div className="text-center">
                <div className="text-lg font-bold mb-1">{process.env.REACT_APP_NAME || 'Arendo'}</div>
                <div className="text-xs opacity-75">{process.env.REACT_APP_DESCRIPTION || 'Arendo'}</div>
              </div>
            )}
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname.split('/')[2] || 'dashboard']}
            items={menuItems}
            onClick={({ key }) => {
              if (isKYCPending && ['finance', 'api-keys', 'snap-v2-credentials', 'cash-flow', 'withdrawal', 'transactions', 'callbacks', 'settings', 'security', 'products'].includes(key)) {
                // Show KYC pending modal for restricted features
                Modal.warning({
                  title: '⚠️ Verifikasi KYC Diperlukan',
                  content: (
                    <div>
                      <p><strong>Status KYC: {merchant?.kyc_status?.toUpperCase()}</strong></p>
                      <br />
                      <p>Fitur <strong>{key.toUpperCase()}</strong> memerlukan verifikasi KYC.</p>
                      <br />
                      <p><strong>Dokumen yang diperlukan:</strong></p>
                      <ul>
                        <li>KTP/SIM/Paspor yang valid</li>
                        <li>NPWP (jika ada)</li>
                        <li>Dokumen kepemilikan usaha</li>
                      </ul>
                      <br />
                      <p><strong>Waktu proses:</strong> 1-3 hari kerja</p>
                      <p><strong>Hubungi:</strong> support@arendo.co.id</p>
                    </div>
                  ),
                  width: 500,
                  okText: 'Mengerti',
                });
              } else {
                navigate(`/merchant/${key}`);
              }
            }}
            style={{
              height: '100%',
              borderRight: 0,
              backgroundColor: 'transparent'
            }}
          />
        </Sider>

        <Layout style={{ marginLeft: collapsed ? '5%' : '15%' }}>
          <Header
            style={{
              padding: '0 32px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'fixed',
              top: 0,
              right: 0,
              left: collapsed ? '5%' : '15%',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="flex items-center space-x-4">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="modern-btn"
                style={{
                  background: 'transparent',
                  color: 'var(--dark)',
                  border: '1px solid var(--gray-light)',
                }}
              />
            </div>

            <div className="flex items-center space-x-6">
              <div className="relative">
                <BellOutlined
                  style={{
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'var(--gray)',
                    transition: 'all 0.3s ease'
                  }}
                  className="hover:color-var(--primary)"
                />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full pulse"></div>
              </div>
              <Dropdown
                menu={{
                  items: userMenuItems.map(item => ({
                    ...item,
                    onClick: () => handleMenuClick(item.key)
                  }))
                }}
                placement="bottomRight"
              >
                <div className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 px-4 py-2 rounded-lg transition-all duration-300">
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <div>
                    <div className="font-semibold text-sm" style={{ color: 'var(--dark)' }}>
                      {merchant?.full_name || 'Merchant'}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--gray)' }}>
                      {merchant?.kyc_status?.toUpperCase() || 'UNVERIFIED'}
                    </div>
                  </div>
                </div>
              </Dropdown>
            </div>
          </Header>

          <Content style={{
            margin: '32px',
            padding: '32px',
            marginTop: 88,
            minHeight: 'calc(100vh - 88px)',
            background: 'transparent'
          }}>
            {/* KYC Status Banner */}
            {isKYCPending && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #fff7e6 0%, #fffbf0 100%)',
                  border: '1px solid #ffd666',
                  borderRadius: '8px',
                  padding: '16px 24px',
                  marginBottom: '24px',
                  boxShadow: '0 2px 8px rgba(250, 173, 20, 0.15)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>⏳</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      color: '#d48806',
                      marginBottom: '4px',
                      fontSize: '16px'
                    }}>
                      Verifikasi KYC Sedang Diproses
                    </div>
                    <div style={{ color: '#8c6a00', fontSize: '14px', lineHeight: '1.4' }}>
                      Account Anda sedang dalam proses verifikasi. Beberapa fitur seperti API Keys dan Tiket Deposit akan tersedia setelah KYC disetujui.
                    </div>
                  </div>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => Modal.info({
                      title: 'Informasi Verifikasi KYC',
                      content: (
                        <div>
                          <h4>Status Verifikasi: <span style={{ color: '#faad14' }}>PENDING</span></h4>
                          <br />
                          <p><strong>Dokumen yang diperlukan:</strong></p>
                          <ul>
                            <li>KTP/SIM/Paspor yang valid</li>
                            <li>NPWP (jika ada)</li>
                            <li>Dokumen kepemilikan usaha</li>
                          </ul>
                          <br />
                          <p><strong>Waktu proses:</strong> 1-3 hari kerja</p>
                          <p><strong>Hubungi:</strong> support@arendo.co.id</p>
                        </div>
                      ),
                      width: 500
                    })}
                  >
                    Lihat Detail
                  </Button>
                </div>
              </div>
            )}
            <Routes>
              <Route path="/" element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<MerchantDashboard />} />
              <Route path="profile" element={<MerchantProfile />} />
              {/* QRIS Payment route - Hidden for now */}
              {/* <Route path="qris-payment" element={<QRISPayment />} /> */}
              <Route path="finance" element={
              <FinanceRouteHandler />
            } />
            <Route path="cash-flow" element={
              <DirectURLHandler>
                <MerchantCashInOut />
              </DirectURLHandler>
            } />
            <Route path="cash-in" element={
              <DirectURLHandler>
                <MerchantCashIn />
              </DirectURLHandler>
            } />
            <Route path="cash-out" element={
              <DirectURLHandler>
                <MerchantCashOut />
              </DirectURLHandler>
            } />
            <Route path="withdrawal" element={
              <DirectURLHandler>
                <MerchantWithdrawal />
              </DirectURLHandler>
            } />
            <Route path="transactions" element={
              <DirectURLHandler>
                <MerchantTransactions />
              </DirectURLHandler>
            } />
            <Route path="payments" element={
              <DirectURLHandler>
                <MerchantPayments />
              </DirectURLHandler>
            } />
            <Route path="callbacks" element={
              <DirectURLHandler>
                <MerchantCallbacks />
              </DirectURLHandler>
            } />
            <Route path="api-keys" element={
              <DirectURLHandler>
                <MerchantAPIKeys />
              </DirectURLHandler>
            } />
            <Route path="products" element={
              <DirectURLHandler>
                <MerchantProducts />
              </DirectURLHandler>
            } />
            <Route path="security" element={
              <DirectURLHandler>
                <MerchantSecurity />
              </DirectURLHandler>
            } />
            <Route path="snap-v2-credentials" element={
              <DirectURLHandler>
                <MerchantSnapV2Credentials />
              </DirectURLHandler>
            } />
            <Route path="settings" element={
              <DirectURLHandler>
                <MerchantSettings />
              </DirectURLHandler>
            } />
            </Routes>
          </Content>
        </Layout>
      </Layout>
  );
}

export default MerchantApp;